/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Loader2, Download, CheckCircle2, Sparkles } from 'lucide-react';

/**
 * BeforeAfterViewer
 *
 * generationPhase controls the progress steps on mobile:
 *   0 = idle / not loading
 *   1 = upload complete, analyzing face active
 *   2 = analyzing complete, applying hairstyle active
 *   3 = applying complete, rendering result active
 *   4 = API returned, rendering still shown briefly before image swap
 */
const BeforeAfterViewer = ({
  beforeImage,
  afterImage,
  loading = false,
  onDownload = null,
  showSuccess = false,
  generationPhase = 0,
  loadingStepLabels: customLoadingStepLabels = null,
}) => {
  const { t } = useTranslation('admin');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDraggingComparison, setIsDraggingComparison] = useState(false);
  const comparisonRef = useRef(null);
  const frameRef = useRef(null);
  const pendingPositionRef = useRef(50);

  useEffect(() => () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
  }, []);

  const scheduleSliderPosition = (nextPosition) => {
    const clamped = Math.min(100, Math.max(0, nextPosition));
    pendingPositionRef.current = clamped;
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      setSliderPosition(pendingPositionRef.current);
      frameRef.current = null;
    });
  };

  const updatePositionFromPointer = (event) => {
    const bounds = comparisonRef.current?.getBoundingClientRect();
    if (!bounds?.width) return;
    scheduleSliderPosition(((event.clientX - bounds.left) / bounds.width) * 100);
  };

  const handleComparisonPointerDown = (event) => {
    if (!comparisonRef.current) return;
    event.preventDefault();
    comparisonRef.current.setPointerCapture?.(event.pointerId);
    setIsDraggingComparison(true);
    updatePositionFromPointer(event);
  };

  const handleComparisonPointerMove = (event) => {
    if (!isDraggingComparison) return;
    updatePositionFromPointer(event);
  };

  const stopComparisonDrag = (event) => {
    comparisonRef.current?.releasePointerCapture?.(event.pointerId);
    setIsDraggingComparison(false);
  };

  const handleDownload = () => {
    if (afterImage && onDownload) onDownload(afterImage);
  };

  const loadingStepLabels = customLoadingStepLabels || [
    t('admin.mobile_generation_upload_received'),
    t('admin.mobile_generation_analyzing_face'),
    t('admin.mobile_generation_applying_style'),
    t('admin.mobile_generation_rendering_result'),
  ];

  // Phase 4 behaves the same as phase 3 visually — "rendering" stays active until
  // the image is ready, so the user never sees all steps green before the result.
  const displayPhase = Math.min(generationPhase, 3);

  const stepState = (index) => {
    if (!loading || generationPhase === 0) return 'pending';
    if (index < displayPhase) return 'complete';
    if (index === displayPhase) return 'active';
    return 'pending';
  };

  return (
    <div className="space-y-3">
      {/* Download Button */}
      {afterImage && !loading && (
        <div className="flex items-center justify-end">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#7d6633] hover:bg-[#6d5b2e] text-white text-xs font-medium transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            {t('admin.download_result')}
          </button>
        </div>
      )}

      <div className="relative">
        {/* ── Loading state ── */}
        {loading && (
          <div className="flex justify-center">
            <div className="w-full max-w-md overflow-hidden rounded-xl bg-zinc-100 dark:bg-zinc-800 border border-[#7d6633]/20">
              <div className="relative aspect-[3/4]">
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-200 via-zinc-100 to-zinc-200 dark:from-zinc-800 dark:via-zinc-700 dark:to-zinc-800 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <div className="w-full rounded-2xl bg-white/90 dark:bg-zinc-900/90 border border-white/60 dark:border-white/10 p-5 text-center shadow-xl backdrop-blur">
                    <Loader2 className="h-10 w-10 animate-spin text-[#7d6633] mx-auto mb-3" />
                    <p className="text-sm text-zinc-700 dark:text-zinc-200 font-semibold">
                      {t('admin.mobile_generation_preview_loading')}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      {t('admin.mobile_generation_time_hint')}
                    </p>
                    <div className="mt-4 space-y-2 text-left">
                      {loadingStepLabels.map((label, index) => {
                        const state = stepState(index);
                        return (
                          <div key={label} className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                            {state === 'complete' ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                            ) : state === 'active' ? (
                              <Loader2 className="h-4 w-4 text-[#7d6633] animate-spin shrink-0" />
                            ) : (
                              <span className="h-4 w-4 rounded-full border border-zinc-300 dark:border-zinc-600 shrink-0 inline-block" />
                            )}
                            <span>{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !beforeImage && !afterImage && (
          <div className="flex justify-center">
            <div className="w-full max-w-md flex items-center justify-center py-20 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
              <div className="text-center text-zinc-400">
                <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium mb-1">{t('admin.mobile_empty_result_title')}</p>
                <p className="text-xs">{t('admin.mobile_empty_result_hint')}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Result / pre-generate state ── */}
        {!loading && (beforeImage || afterImage) && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[#7d6633] dark:text-[#a0824a] uppercase tracking-wide">
                {afterImage ? t('admin.generated_with_new_haircut') : t('admin.awaiting_generation')}
              </span>
            </div>

            {/* Desktop-only success banner */}
            {afterImage && showSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="hidden md:flex items-center justify-center gap-2 rounded-xl border border-[#d4b47a]/40 bg-[#d4b47a]/10 px-3 py-2 text-sm font-semibold text-[#6a542a] dark:text-[#f1d999]"
              >
                <Sparkles className="h-4 w-4" />
                <span>{t('admin.mobile_generation_ready')}</span>
              </motion.div>
            )}

            {/* Mobile Before/After comparison slider */}
            {afterImage && beforeImage && (
              <div className="md:hidden space-y-3">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  <span>{t('admin.label_original')}</span>
                  <span>{t('admin.generated_hairstyle_label')}</span>
                </div>
                <div
                  ref={comparisonRef}
                  onPointerDown={handleComparisonPointerDown}
                  onPointerMove={handleComparisonPointerMove}
                  onPointerUp={stopComparisonDrag}
                  onPointerCancel={stopComparisonDrag}
                  className={`relative w-full max-w-md mx-auto aspect-[3/4] rounded-xl overflow-hidden bg-zinc-200 dark:bg-zinc-800 border-2 border-[#7d6633]/30 cursor-ew-resize select-none touch-none ${
                    isDraggingComparison ? 'border-[#d4b47a]/70 shadow-[0_0_22px_rgba(212,180,122,0.35)]' : ''
                  }`}
                >
                  <img
                    src={beforeImage}
                    alt={t('admin.label_original')}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <motion.div
                    className="absolute inset-0 overflow-hidden"
                    style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.35 }}
                  >
                    <img
                      src={afterImage}
                      alt={t('admin.generated_hairstyle_label')}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>

                  {/* Divider + handle — share the exact same left position */}
                  <div
                    className={`absolute top-0 bottom-0 w-10 cursor-ew-resize ${
                      isDraggingComparison ? 'drop-shadow-[0_0_10px_rgba(212,180,122,0.9)]' : ''
                    }`}
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                  >
                    {/* Divider line */}
                    <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.2)]" />
                    {/* Handle — centred over the line via its own translate */}
                    <div className="absolute top-1/2 left-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2">
                      <motion.div
                        animate={{
                          scale: isDraggingComparison ? 1.14 : 1,
                          boxShadow: isDraggingComparison
                            ? '0 0 0 6px rgba(212,180,122,0.22), 0 10px 24px rgba(0,0,0,0.28)'
                            : '0 8px 18px rgba(0,0,0,0.22)',
                        }}
                        transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                        className="h-full w-full rounded-full bg-white text-[#7d6633] flex items-center justify-center text-xs font-bold border border-[#d4b47a]/40"
                      >
                        &lt;&gt;
                      </motion.div>
                    </div>
                  </div>

                  {/* Bottom range input — stop propagation so it doesn't fire the outer pointer down */}
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={sliderPosition}
                    onChange={(e) => scheduleSliderPosition(Number(e.target.value))}
                    aria-label={t('admin.mobile_before_after_slider')}
                    className="absolute inset-x-4 bottom-4 accent-[#7d6633] cursor-ew-resize"
                    onPointerDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            {/* Desktop result / pre-generate placeholder */}
            <div className={`${afterImage && beforeImage ? 'hidden md:flex' : 'flex'} justify-center`}>
              <div className="relative w-full max-w-md aspect-[3/4] rounded-xl overflow-hidden bg-zinc-200 dark:bg-zinc-800 border-2 border-[#7d6633]/30">
                {afterImage ? (
                  <motion.img
                    src={afterImage}
                    alt={t('admin.generated_hairstyle_label')}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.35 }}
                  />
                ) : beforeImage ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img src={beforeImage} alt={t('admin.label_original')} className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center bg-white/90 dark:bg-zinc-900/90 p-6 rounded-xl backdrop-blur-sm">
                        <ImageIcon className="h-12 w-12 opacity-50 mx-auto mb-2 text-zinc-400" />
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('admin.ready_to_generate')}</p>
                        <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">{t('admin.click_generate_hairstyle')}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                    <div className="text-center">
                      <ImageIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
                      <p className="text-xs">{t('admin.result_will_appear_here')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeforeAfterViewer;
